/**
 * Grammar quiz component.
 *
 * Interactive quiz component for grammar topics. Displays fill-in-the-blank
 * questions, handles answer input, provides answer checking, and shows results
 * with statistics. Supports showing expected answers and quiz reset.
 *
 * @module GrammarQuiz
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Eye, RotateCcw } from 'lucide-react';
import {
  type GrammarQuiz,
  type QuizQuestion,
  type QuizSection,
  type QuizBlank,
} from '@/lib/contentLoader';
import { grammarLevels } from '@/data/learningData';
import { cn } from '@/lib/utils';
import { useGrammarQuizStore } from '@/stores/useGrammarQuizStore';
import { sanitizeHtml } from '@/lib/sanitize';
import { ROUTES } from '@/lib/routeConstants';
import { useTranslation } from 'react-i18next';

interface GrammarQuizProps {
  readonly quiz: GrammarQuiz;
  readonly level: string;
  readonly topicId: string;
}

/**
 * Component for displaying and managing grammar practice quiz
 */
export function GrammarQuiz({ quiz, level, topicId }: GrammarQuizProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    quizAnswers,
    showResults,
    checkedQuestions,
    shownAnswers,
    checkQuestion,
    showAnswer,
    submitQuiz,
    setAnswer,
    resetQuiz,
    calculateStats,
    getAllAnswered,
  } = useGrammarQuizStore();

  // Calculate stats
  const { total: totalBlanks, correct: correctBlanks } = calculateStats(quiz);
  const totalQuestions =
    (quiz.questions?.filter((q: QuizQuestion) => q.blanks && q.blanks.length > 0).length || 0) +
    (quiz.sections?.reduce(
      (sum: number, s: QuizSection) =>
        sum + s.questions.filter((q: QuizQuestion) => q.blanks && q.blanks.length > 0).length,
      0,
    ) || 0);

  const allAnswered = getAllAnswered(quiz);

  // Find next topic
  const currentLevelIndex = grammarLevels.findIndex((l) => l.level === level);
  const currentTopicIndex =
    grammarLevels[currentLevelIndex]?.topics.findIndex((t) => t.id === topicId) ?? -1;

  const nextTopic =
    currentTopicIndex >= 0 &&
      currentTopicIndex < grammarLevels[currentLevelIndex].topics.length - 1
      ? grammarLevels[currentLevelIndex].topics[currentTopicIndex + 1]
      : null;

  // Helper function to calculate dynamic width based on content
  const getDynamicWidth = (content: string, isAnswerDisplay = false) => {
    const baseWidth = 80; // minimum width in pixels
    const charWidth = 9; // approximate width per character
    const padding = 20; // padding for the input
    const contentLength = Math.max(content.length, isAnswerDisplay ? 6 : 4); // minimum visible chars
    const calculatedWidth = Math.max(baseWidth, contentLength * charWidth + padding);
    const maxWidth = typeof globalThis !== 'undefined' && globalThis.innerWidth < 640 ? 250 : 400;
    return `${Math.min(calculatedWidth, maxWidth)}px`;
  };

  // Render quiz question
  const renderQuizQuestion = (question: QuizQuestion, questionId: string, index: number) => {
    // Check if question has blanks (fill-in-the-blank type)
    if (!question.blanks || question.blanks.length === 0) {
      // Text-only question (dialogue or instruction)
      return (
        <div key={questionId} className="bg-card border-2 border-border rounded-xl p-4 sm:p-6 mb-6">
          <div className="flex items-start gap-2">
            <span className="font-ui text-sm text-muted-foreground flex-shrink-0 w-8">
              {index}
            </span>
            <div className="text-base sm:text-lg font-medium flex-1">
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    question.text
                      .replaceAll(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replaceAll(/<u>(.*?)<\/u>/g, '<u>$1</u>')
                      .replaceAll('\n', '<br>'),
                  ),
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    // Fill-in-the-blank question - split by ___ markers
    const parts = question.text.split('___');
    const questionAnswers = quizAnswers[questionId] ?? [];
    const questionChecked = checkedQuestions[questionId] || [];
    const hasResult = showResults || questionChecked.length > 0;
    const isAnswerShown = shownAnswers[questionId] || false;

    return (
      <div key={questionId} className="bg-card border-2 border-border rounded-xl p-4 sm:p-6 mb-6">
        <div className="flex items-start gap-2">
          <span className="font-ui text-sm text-muted-foreground flex-shrink-0 w-8">
            {index}
          </span>
          <div className="text-base sm:text-lg font-medium flex-1">
            {parts.map((part, partIndex) => {
              // Render markdown formatting for each part
              let formattedPart = part.replaceAll(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              formattedPart = formattedPart.replaceAll(/<u>(.*?)<\/u>/g, '<u>$1</u>');
              formattedPart = formattedPart.replaceAll('\n', '<br>');

              // Sanitize HTML to prevent XSS attacks while preserving formatting
              const safeFormattedPart = sanitizeHtml(formattedPart);

              // Last part doesn't have an input after it
              if (partIndex === parts.length - 1) {
                return (
                  <span
                    key={`${questionId}-part-${partIndex}`}
                    dangerouslySetInnerHTML={{ __html: safeFormattedPart }}
                  />
                );
              }

              const blankIndex = partIndex;
              const blank =
                question.blanks.find((b: QuizBlank) => b.placeholder === blankIndex) ||
                question.blanks[blankIndex];
              const inputValue = questionAnswers[blankIndex] || '';
              const expectedAnswer = blank?.acceptedAnswers?.[0] || '';
              const isCorrect = hasResult && questionChecked[blankIndex];
              const isWrong = hasResult && !isCorrect;

              // Calculate dynamic width for input
              const inputWidth = getDynamicWidth(inputValue || expectedAnswer);

              return (
                <span key={`${questionId}-part-${partIndex}-blank`} className="inline">
                  <span dangerouslySetInnerHTML={{ __html: safeFormattedPart }} />
                  <span className="inline-block mx-1 align-middle">
                    {isAnswerShown && !inputValue ? (
                      <span
                        className={cn(
                          'inline-block px-1 py-0 text-center text-sm border-0 border-b-2',
                          'bg-transparent outline-none border-blue-500 text-blue-700',
                        )}
                        style={{ width: inputWidth, minWidth: '60px' }}
                      >
                        {expectedAnswer}
                      </span>
                    ) : (
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) =>
                          setAnswer(
                            questionId,
                            blankIndex,
                            e.target.value,
                            question.blanks.length,
                          )
                        }
                        disabled={hasResult || isAnswerShown}
                        className={cn(
                          'inline-block px-1 py-0 text-center text-sm',
                          'border-0 border-b-2 bg-transparent outline-none',
                          'focus:border-primary focus:border-b-2',
                          'transition-colors duration-200',
                          !hasResult && !isAnswerShown && 'border-border',
                          isCorrect && 'border-green-500 text-green-700',
                          isWrong && 'border-red-500 text-red-700',
                          isAnswerShown && 'border-blue-500 text-blue-700',
                        )}
                        style={{ width: inputWidth, minWidth: '60px' }}
                      />
                    )}
                    {hasResult && (
                      <span className="ml-1 align-middle">
                        {isCorrect ? (
                          <span className="inline-block w-4 h-4 text-green-500">✓</span>
                        ) : (
                          <span className="inline-block w-4 h-4 text-red-500">✗</span>
                        )}
                      </span>
                    )}
                  </span>
                </span>
              );
            })}
          </div>
        </div>

        {hasResult &&
          question.blanks.some(
            (blank: QuizBlank, idx: number) => !questionChecked[idx] && blank.explanation,
          ) && (
            <div className="text-sm text-muted-foreground mt-4 ml-0 sm:ml-10 bg-muted/30 p-3 rounded-lg border border-border/50">
              <p className="font-medium text-primary mb-2">Explanation:</p>
              <ul className="list-disc pl-5 space-y-1">
                {question.blanks.map((blank: QuizBlank, idx: number) => {
                  if (!questionChecked[idx] && blank.explanation) {
                    return (
                      <li key={`${questionId}-blank-${idx}-explanation`}>{blank.explanation}</li>
                    );
                  }
                  return null;
                })}
              </ul>
            </div>
          )}

        {/* Action buttons for each question */}
        <div className="flex flex-wrap items-center gap-2 mt-4 ml-0 sm:ml-10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => checkQuestion(questionId, quiz)}
            disabled={hasResult}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-xs sm:text-sm">{t('learning.checkAnswer', { defaultValue: 'Check' })}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => showAnswer(questionId)}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2"
          >
            <Eye className="h-4 w-4" />
            <span className="text-xs sm:text-sm">
              {isAnswerShown
                ? t('learning.hideAnswer', { defaultValue: 'Hide' })
                : t('learning.showAnswer', { defaultValue: 'Show' })}
            </span>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {showResults ? (
        <div className="bg-card border-2 border-border rounded-xl p-8 text-center">
          <h2 className="font-ui text-2xl font-semibold text-foreground mb-2">Quiz Complete!</h2>
          <p className="font-ui text-lg text-foreground mb-6">
            You got {correctBlanks} out of {totalBlanks} blanks correct
            {totalQuestions > 0 && ` (${totalQuestions} questions)`}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button variant="outline" onClick={resetQuiz} className="w-full sm:w-auto">
              {t('learning.retry', { defaultValue: 'Retry quiz' })}
            </Button>
            <Button onClick={() => navigate(ROUTES.LEARNING_GRAMMAR)} className="w-full sm:w-auto">
              {t('learning.backOverview', { defaultValue: 'Back to Overview' })}
            </Button>
            {nextTopic && (
              <Button onClick={() => navigate(`/learning/grammar/${level}/${nextTopic.id}`)} className="w-full sm:w-auto">
                {t('learning.nextTopic', { defaultValue: 'Next topic' })}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-ui text-xl sm:text-2xl font-semibold text-foreground mb-1">
                {t('learning.practiceQuiz', { defaultValue: 'Practice Quiz' })}
              </h2>
              <p className="font-ui text-sm text-muted-foreground">{t('learning.questionsCount', { count: totalQuestions, defaultValue: '{{count}} questions' })}</p>
            </div>
            <Button variant="outline" size="sm" onClick={resetQuiz} className="w-full sm:w-auto">
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('learning.resetQuiz', { defaultValue: 'Reset quiz' })}
            </Button>
          </div>

          <div className="space-y-6">
            {quiz.questions?.map((question, index) =>
              renderQuizQuestion(question, question.id, index + 1),
            )}
            {quiz.sections?.map((section, sectionIndex) => {
              // Calculate global question index for sections
              const questionsBefore =
                (quiz.questions?.length || 0) +
                (quiz.sections?.slice(0, sectionIndex).reduce(
                  (sum: number, s: QuizSection) => sum + s.questions.length,
                  0,
                ) || 0);

              return (
                <div key={section.id} className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 text-primary border-b pb-2">
                    {section.title}
                  </h3>
                  <div className="space-y-6">
                    {section.questions.map((question, questionIndex) =>
                      renderQuizQuestion(
                        question,
                        question.id,
                        questionsBefore + questionIndex + 1,
                      ),
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {allAnswered && (
            <div className="mt-6 flex justify-center">
              <Button size="lg" onClick={() => submitQuiz(quiz)}>
                View Results
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

